import React, { useState, useEffect, useContext } from "react";
import cx from "classnames";

import { hasItems, pluralize } from "../../../../../helpers/Utils";
import useMediaQuery from "../../../../../helpers/useMediaQuery";

import FilterIcon from "../../../../../components/Icon/icons/general/FilterIcon";
import WeaveSearchWidget from "../../../../../components/WeaveSearchWidget/WeaveSearchWidget";
import MarketPlaceContext from "../../MarketPlaceContext";

import MarketCategories from "../../../../../data/types/MarketCategories";
import FiltersDialog from "./FiltersDialog";
import MarketFilterCategories from "./MarketFilterCategories";
import ResetFiltersButton from "./ResetFiltersButton";
import FilterPill from "./FilterPill";

import s from "./MarketFilters.module.scss";

const ALL_CATEGORY = {
	name: "All",
	value: "all",
	isSelected: true,
};

export default function MarketFilters() {
	const { filters = {}, filterResults = () => {} } =
		useContext(MarketPlaceContext);

	const [showCategories, setShowCategories] = useState(false);
	const [showFiltersDialog, setShowFiltersDialog] = useState(false);
	const [filterCategories, setFilterCategories] = useState([]);
	const [countFilters, setCountFilters] = useState(0);
	const [searchText, setSearchText] = useState("");

	const isMobile = useMediaQuery("(max-width: 700px)");

	useEffect(() => {
		initFilters();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters]);

	// ------------------------------------- METHODS -------------------------------------
	const initFilters = () => {
		const { categories: selectedCategories = [] } = filters;

		const count = selectedCategories.length;
		setCountFilters(count);

		if (!showCategories) setShowCategories(count > 0);

		const categories = Object.keys(MarketCategories).map((c) => ({
			value: c,
			name: MarketCategories[c],
			isSelected: selectedCategories.includes(c),
		}));

		setFilterCategories([
			{ ...ALL_CATEGORY, isSelected: count === 0 },
			...categories,
		]);
	};

	const filterByKeyword = (val) => {
		const categories = filterCategories
			.filter((c) => c.isSelected && c.value !== "all")
			.map((c) => c.value);

		setSearchText(val);
		filterResults({ searchText: val, categories });
	};

	const toggleCategory = (c) => {
		setShowFiltersDialog(false);

		if (c.value === "all") return resetFilters();

		let selected = [];
		let newCategories = [];
		filterCategories.forEach((cat) => {
			const model =
				cat.value === c.value ? { ...c, isSelected: !c.isSelected } : cat;
			newCategories.push(model);

			// Deselect all
			if (!c.isSelected && model.value === "all") {
				model.isSelected = false;
			}

			if (model.isSelected && model.value !== "all") {
				selected.push(model.value);
			}
		});

		// Select ALL if nothing else
		if (!hasItems(selected)) {
			newCategories = newCategories.map((c) =>
				c.value === "all"
					? {
							...c,
							isSelected: true,
					  }
					: c
			);
		}

		setFilterCategories([...newCategories]);
		filterResults({ categories: selected, searchText });
	};

	const resetFilters = () => {
		filterResults({}, true);
		setShowFiltersDialog(false);
	};

	const toggleFiltersContainer = () => {
		if (isMobile) {
			setShowFiltersDialog(true);
		} else {
			setShowCategories(!showCategories);
		}
	};

	return (
		<div className={s.root}>
			<div className={s.searchContainer}>
				<FilterPill
					onClick={toggleFiltersContainer}
					isSelected={!!showCategories}
					className={s.pill}
				>
					<FilterIcon
						color={showCategories ? "#f5fafe" : "#50b0f9"}
						width={15}
						height={15}
					/>
					<p>
						{countFilters === 0 || !isMobile
							? "Categories"
							: `${pluralize("Filter", countFilters)} Active`}
					</p>
				</FilterPill>

				<div className={s.search}>
					<WeaveSearchWidget
						text={filters.searchText || ""}
						delay={500}
						placeholder="Search"
						onChange={filterByKeyword}
					/>
				</div>
			</div>

			{showFiltersDialog && (
				<FiltersDialog
					categories={filterCategories}
					toggleCategory={toggleCategory}
					resetFilters={resetFilters}
					close={() => setShowFiltersDialog(false)}
				/>
			)}

			<div className={cx(s.filters, showCategories ? "" : s.hidden)}>
				<MarketFilterCategories
					categories={filterCategories}
					toggleCategory={toggleCategory}
					resetFilters={resetFilters}
				/>

				<ResetFiltersButton resetFilters={resetFilters} />
			</div>
		</div>
	);
}
